"use client";

import {
	faArrowsRotate,
	faCircleUp,
	faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";
import { useTranslations } from "next-intl";
import { useEffect, useSyncExternalStore } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/Icon";
import {
	checkForUpdate,
	getAppUpdateServerSnapshot,
	getAppUpdateSnapshot,
	reloadForUpdate,
	setServedBuild,
	snoozeAppUpdate,
	subscribeToAppUpdate,
} from "@/lib/app-update";

/**
 * "Jana was updated — reload." The one visible surface of deploy detection, and
 * the only thing in the tree that watches for one.
 *
 * ## Where it lives, and why it is one component rather than three
 *
 * It is mounted once, in the root layout, so it covers the admin panel, the
 * store panel, the four auth screens, the chooser, the invoice pages and the
 * whole storefront — all of which post server actions and none of which shares a
 * shell (§ Layout: `/manage/change-password` and `(shop)` render outside both
 * panels, which is the same argument that put `DirectionProvider` at the root).
 * A per-shell copy would miss exactly the pages that render outside one.
 *
 * ⚠️ **It is deliberately NOT a sonner toast**, which is the tempting spelling
 * since the panels already have a `<Toaster/>`. Two reasons, and the second is
 * the real one: the storefront mounts no Toaster at all, so half the app would
 * be silent — and a toast is a transient thing the user is trained to let expire,
 * while this must still be there ten minutes later when they finally press Save.
 *
 * ## Two tones
 *
 * - **stale** — a new build is live and nothing has failed yet. Quiet, corner,
 *   dismissible ("Later" hides it for 30 minutes).
 * - **blocked** — an action was already refused. Destructive tone, no dismiss.
 *   In practice the error boundary usually owns the screen in this case and this
 *   stays hidden (`claimUpdateScreen`); the state is still rendered here for a
 *   caller that catches its own action failure without swapping the screen.
 *
 * ⚠️ **Neither tone ever reloads by itself.** A merchant with a half-written
 * product description is precisely who a redeploy interrupts, and `<form action>`
 * state is not recoverable across a reload. The button is the whole mechanism.
 *
 * ## Placement
 *
 * Bottom **start**, because sonner is pinned bottom-end in both panels
 * (`ui/sonner.tsx` flips the physical edge per locale) and stacking a persistent
 * card under the toast column would cover it. `start-4` is logical, so this
 * lands on the opposite side in Arabic for free — the same flip, without a
 * second place deciding direction.
 */

/**
 * Five minutes. It is a floor, not the resolution: `visibilitychange` and
 * `focus` are what actually catch a deploy in practice — the tab a merchant left
 * open over lunch checks the moment they come back to it — and the interval is
 * only there for a tab that is watched continuously without ever being blurred.
 * A tighter one buys nothing and multiplies by every open tab.
 */
const POLL_MS = 5 * 60_000;

export function AppUpdateBanner({ build }: { build: string }) {
	const t = useTranslations("common.appUpdate");
	const { stale, blocked } = useSyncExternalStore(
		subscribeToAppUpdate,
		getAppUpdateSnapshot,
		getAppUpdateServerSnapshot,
	);

	useEffect(() => {
		setServedBuild(build);

		// No check on mount: this document was just served by the build it is
		// comparing against, so the answer is known. The first useful moment is the
		// first time the tab is re-focused or the interval fires.
		const wake = () => {
			if (document.visibilityState === "visible") void checkForUpdate();
		};
		const timer = setInterval(wake, POLL_MS);
		document.addEventListener("visibilitychange", wake);
		globalThis.addEventListener("focus", wake);
		return () => {
			clearInterval(timer);
			document.removeEventListener("visibilitychange", wake);
			globalThis.removeEventListener("focus", wake);
		};
	}, [build]);

	if (!stale && !blocked) return null;

	return (
		<div className="pointer-events-none fixed inset-x-4 bottom-4 z-50 flex justify-center sm:inset-x-auto sm:start-4 sm:justify-start print:hidden">
			<Alert
				// `blocked` is an interruption the user must act on; `stale` is an
				// offer. `Alert` hardcodes role="alert", which is right for the first
				// and shouts over a screen reader mid-sentence for the second.
				role={blocked ? "alert" : "status"}
				variant={blocked ? "destructive" : "default"}
				className="pointer-events-auto w-full max-w-sm shadow-lg"
			>
				<Icon icon={blocked ? faTriangleExclamation : faCircleUp} size={16} />
				<AlertTitle>{blocked ? t("blockedTitle") : t("title")}</AlertTitle>
				<AlertDescription>{blocked ? t("blockedBody") : t("body")}</AlertDescription>
				<div className="col-start-2 mt-2 flex flex-wrap gap-2">
					<Button size="sm" onClick={reloadForUpdate}>
						<Icon icon={faArrowsRotate} size={14} />
						{t("reload")}
					</Button>
					{!blocked && (
						<Button size="sm" variant="ghost" onClick={snoozeAppUpdate}>
							{t("later")}
						</Button>
					)}
				</div>
			</Alert>
		</div>
	);
}
